import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardList,
  Loader2,
  Lock,
  Sparkles,
  Trophy,
  X,
  XCircle,
} from 'lucide-react';
import { QuizQuestion, invalidateCourseProgressCaches } from '../../hooks/useCourseApi';
import { submitQuizResult } from '../../services/courseService';
import { cardTheme } from '../../styles/cardTheme';

const TRUE_LABELS = new Set([
  'true',
  't',
  'yes',
  '1',
  'صح',
  'صحيح',
  'حق',
]);
const FALSE_LABELS = new Set([
  'false',
  'f',
  'no',
  '0',
  'خطأ',
  'خطا',
  'غلط',
  'خطأ.',
]);

const isTrueFalseQuestion = (
  question: Pick<QuizQuestion, 'type' | 'answers'>
): boolean => {
  const type = String(question.type ?? '').trim().toLowerCase();
  if (
    type === 'truefalse' ||
    type === 'true_false' ||
    type === 'trueorfalse' ||
    type === '2' ||
    type === '1'
  ) {
    return true;
  }

  if (question.answers.length !== 2) return false;
  return question.answers.every((answer) => {
    const label = String(answer.text ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    return TRUE_LABELS.has(label) || FALSE_LABELS.has(label);
  });
};

/** Display True/False answers in Arabic: صح / خطأ */
const formatAnswerText = (text: string, isTrueFalse: boolean): string => {
  if (!isTrueFalse) return text;
  const normalized = String(text ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (TRUE_LABELS.has(normalized)) return 'صح';
  if (FALSE_LABELS.has(normalized)) return 'خطأ';
  return text;
};

interface QuizContentProps {
  questions: QuizQuestion[];
  onComplete: () => void;
  /** Required for graded course quizzes; optional in practice mode */
  lessonId?: number;
  /** When true, quiz was already submitted — retake is blocked */
  alreadySubmitted?: boolean;
  /** Optional course id — used to invalidate progress cache after submit */
  courseId?: string;
  /**
   * Practice / configured quiz: score is local only (no API submit, no progress).
   * Student can finish and leave without locking a lesson.
   */
  practiceMode?: boolean;
}

export const QuizContent: React.FC<QuizContentProps> = ({
  questions,
  onComplete,
  lessonId,
  alreadySubmitted = false,
  courseId,
  practiceMode = false,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<(number | undefined)[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedThisSession, setSubmittedThisSession] = useState(false);

  const isLocked = alreadySubmitted || submittedThisSession || showResults;

  const answeredCount = useMemo(
    () => selectedAnswers.filter((a) => a !== undefined).length,
    [selectedAnswers]
  );
  const allAnswered = questions.length > 0 && answeredCount === questions.length;

  const handleAnswerSelect = (answerIndex: number) => {
    if (isLocked) return;
    setSelectedAnswers((prev) => {
      const next = [...prev];
      next[currentQuestion] = answerIndex;
      return next;
    });
  };

  const goToQuestion = (index: number) => {
    if (index < 0 || index >= questions.length) return;
    setCurrentQuestion(index);
    setError(null);
  };

  const calculateScore = () => {
    let correctAnswers = 0;
    selectedAnswers.forEach((selectedIndex, questionIndex) => {
      if (
        selectedIndex !== undefined &&
        questions[questionIndex].answers[selectedIndex]?.isCorrect
      ) {
        correctAnswers++;
      }
    });
    return {
      correct: correctAnswers,
      total: questions.length,
      percentage: Math.round((correctAnswers / questions.length) * 100),
    };
  };

  const handleSubmit = async () => {
    if (isLocked || !allAnswered || submitting) return;

    setSubmitting(true);
    setError(null);
    const score = calculateScore();

    try {
      if (practiceMode) {
        setSubmittedThisSession(true);
        setShowResults(true);
        return;
      }

      if (lessonId == null) {
        setError('معرّف الدرس غير متوفر');
        return;
      }

      await submitQuizResult({
        lessonId,
        score: score.percentage,
        notes: `Correct answers: ${score.correct}/${score.total}`,
      });
      invalidateCourseProgressCaches(courseId, lessonId);
      setSubmittedThisSession(true);
      setShowResults(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'فشل في إرسال نتيجة الاختبار';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadySubmitted && !showResults) {
    return (
      <div className="w-full max-w-full min-w-0 space-y-4 font-cairo sm:space-y-5" dir="rtl">
        <div className={`${cardTheme.shell} overflow-hidden text-center`}>
          <div className={`${cardTheme.headerBanner} px-4 py-8 sm:px-6 sm:py-10`}>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-gold-300 sm:h-20 sm:w-20">
              <Lock className="h-8 w-8 sm:h-10 sm:w-10" />
            </div>
            <h3 className="mb-2 text-xl font-bold text-white sm:text-2xl">تم تسليم الاختبار مسبقاً</h3>
            <p className="text-sm text-green-100/90 sm:text-base">
              لا يمكنك إعادة حل هذا الاختبار بعد التسليم
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <p className={`mb-4 text-sm sm:text-base ${cardTheme.subtitle}`}>
              يمكنك متابعة باقي الدروس من قائمة المنهج
            </p>
            <button
              type="button"
              onClick={onComplete}
              className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold sm:min-h-12 sm:w-auto sm:min-w-[12rem] sm:text-base ${cardTheme.btnGold}`}
            >
              متابعة
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className={`${cardTheme.shell} p-8 text-center`} dir="rtl">
        <ClipboardList className="mx-auto mb-3 h-10 w-10 text-gray-300" />
        <p className="text-sm text-gray-600 sm:text-base">لا توجد أسئلة متاحة</p>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const isTrueFalse = isTrueFalseQuestion(question);
  const score = showResults ? calculateScore() : null;
  const progressPct = Math.round((answeredCount / questions.length) * 100);
  const hasSelection = selectedAnswers[currentQuestion] !== undefined;
  const isLast = currentQuestion === questions.length - 1;
  const isFirst = currentQuestion === 0;

  const getAnswerStatusClass = (questionIndex: number, answerIndex: number) => {
    const isSelected = selectedAnswers[questionIndex] === answerIndex;
    const isCorrect = questions[questionIndex].answers[answerIndex].isCorrect;

    if (isSelected && isCorrect) return 'border-green-600 bg-green-50 text-green-900';
    if (isSelected && !isCorrect) return 'border-red-400 bg-red-50 text-red-800';
    if (!isSelected && isCorrect) return 'border-gold-400 bg-gold-50 text-green-900';
    return 'border-green-100 bg-white text-gray-700';
  };

  return (
    <div className="w-full max-w-full min-w-0 overflow-x-hidden font-cairo" dir="rtl">
      {!showResults ? (
        <div className="w-full max-w-full min-w-0 space-y-4 sm:space-y-5">
          {/* Progress header */}
          <div className={`${cardTheme.shell} overflow-hidden`}>
            <div className={`${cardTheme.headerBanner} px-4 py-4 sm:px-5 sm:py-5`}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <span
                  className={`inline-flex shrink-0 ${cardTheme.badgeGold} border-gold-400/40 bg-gold-500/20 text-gold-200`}
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  {practiceMode ? 'تدريب' : 'اختبار'}
                </span>
                <span className="shrink-0 text-xs font-semibold text-green-100 sm:text-sm">
                  {answeredCount}/{questions.length} مجاب
                </span>
              </div>
              <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
                <h3 className="min-w-0 truncate text-base font-bold text-white sm:text-lg">
                  السؤال {currentQuestion + 1} من {questions.length}
                </h3>
                <span className="shrink-0 text-xs text-green-100/80 sm:text-sm">{progressPct}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/15">
                <div
                  className="h-full rounded-full bg-gold-400 transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Question number pagination — mobile scrollable */}
          <div className={`${cardTheme.shell} p-3 sm:p-4`}>
            <p className={`mb-2.5 text-xs font-semibold sm:text-sm ${cardTheme.subtitle}`}>
              التنقل بين الأسئلة
            </p>
            <div
              className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]"
              role="tablist"
              aria-label="أرقام الأسئلة"
            >
              {questions.map((_, index) => {
                const answered = selectedAnswers[index] !== undefined;
                const active = index === currentQuestion;
                return (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`السؤال ${index + 1}${answered ? ' — مجاب' : ''}`}
                    onClick={() => goToQuestion(index)}
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold transition-all sm:h-11 sm:w-11 ${
                      active
                        ? 'bg-green-800 text-white shadow-md shadow-green-800/25'
                        : answered
                          ? 'border border-gold-300 bg-gold-50 text-gold-800'
                          : 'border border-green-100 bg-white text-gray-600 hover:border-green-300 hover:bg-green-50/50'
                    }`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question card */}
          <div
            className={`${cardTheme.shell} ${cardTheme.accentTop} w-full max-w-full min-w-0 overflow-hidden p-4 sm:p-6`}
          >
            <p
              className={`mb-4 break-words text-base font-bold leading-relaxed sm:mb-5 sm:text-lg ${cardTheme.title}`}
            >
              {question.text}
            </p>

            <div className="w-full max-w-full min-w-0 space-y-2.5 sm:space-y-3">
              {question.answers.map((answer, index) => {
                const selected = selectedAnswers[currentQuestion] === index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAnswerSelect(index)}
                    disabled={isLocked}
                    className={`flex w-full max-w-full min-h-12 min-w-0 items-center gap-3 rounded-xl border-2 p-3.5 text-right transition-all sm:min-h-14 sm:p-4 disabled:cursor-not-allowed ${
                      selected
                        ? 'border-gold-400 bg-gold-50 shadow-sm shadow-gold-500/20'
                        : 'border-green-100 bg-white hover:border-green-300 hover:bg-green-50/40'
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold sm:h-9 sm:w-9 ${
                        selected ? cardTheme.iconBoxGold : 'bg-green-50 text-green-800'
                      }`}
                    >
                      {selected ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                    </span>
                    <span
                      className={`min-w-0 flex-1 break-words text-sm font-medium leading-snug sm:text-base ${
                        selected ? 'text-green-950' : 'text-gray-800'
                      }`}
                    >
                      {formatAnswerText(answer.text, isTrueFalse)}
                    </span>
                    {selected ? (
                      <Circle className="h-4 w-4 shrink-0 fill-gold-500 text-gold-500" />
                    ) : (
                      <Circle className="h-4 w-4 shrink-0 text-gray-300" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
              <X className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">{error}</span>
            </div>
          )}

          {/* Prev / Next + Submit */}
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => goToQuestion(currentQuestion - 1)}
                disabled={isFirst}
                className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-green-200 bg-white px-3 text-sm font-semibold text-green-800 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-12 sm:text-base"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                السابق
              </button>
              <button
                type="button"
                onClick={() => goToQuestion(currentQuestion + 1)}
                disabled={isLast}
                className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-green-200 bg-white px-3 text-sm font-semibold text-green-800 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-12 sm:text-base"
              >
                التالي
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting || isLocked}
              className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold sm:min-h-14 sm:text-base disabled:cursor-not-allowed disabled:opacity-50 ${cardTheme.btnPrimary}`}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {practiceMode ? 'جاري التصحيح...' : 'جاري الإرسال...'}
                </>
              ) : (
                <>
                  <Trophy className="h-5 w-5" />
                  {allAnswered
                    ? practiceMode
                      ? 'إنهاء وعرض النتيجة'
                      : 'تسليم الاختبار'
                    : `أجب عن جميع الأسئلة (${answeredCount}/${questions.length})`}
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-full min-w-0 space-y-4 sm:space-y-5">
          <div className={`${cardTheme.shell} w-full max-w-full overflow-hidden text-center`}>
            <div className={`${cardTheme.headerBanner} px-4 py-8 sm:px-6 sm:py-10`}>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-500 text-green-950 shadow-lg shadow-black/20 sm:h-20 sm:w-20">
                <Trophy className="h-8 w-8 sm:h-10 sm:w-10" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-white sm:text-2xl">
                {practiceMode ? 'انتهى التدريب!' : 'تم إكمال الاختبار!'}
              </h3>
              <p className="text-sm text-green-100/90 sm:text-base">
                {practiceMode
                  ? 'نتيجة للتدريب فقط — لا تُحتسب ضمن تقدّم الدورة'
                  : 'تم التسليم — لا يمكن إعادة حل هذا الاختبار'}
              </p>
            </div>

            {score && (
              <div className="p-4 sm:p-6">
                <div className={`mb-1 text-3xl font-bold sm:text-4xl ${cardTheme.price}`}>
                  {score.percentage}%
                </div>
                <p className={`mb-4 text-sm sm:text-base ${cardTheme.subtitle}`}>
                  {score.correct} إجابات صحيحة من {score.total}
                </p>
                <div className="mx-auto h-2.5 max-w-xs overflow-hidden rounded-full bg-green-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-l from-green-700 to-gold-500 transition-all duration-1000"
                    style={{ width: `${score.percentage}%` }}
                  />
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  <span className={cardTheme.badge}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {score.correct} صحيح
                  </span>
                  <span className={cardTheme.badgeGold}>
                    <Sparkles className="h-3.5 w-3.5" />
                    {practiceMode ? 'نتيجة التدريب' : 'النتيجة النهائية'}
                  </span>
                  {!practiceMode && (
                    <span className={cardTheme.badge}>
                      <Lock className="h-3.5 w-3.5" />
                      مُغلق
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {!showAnswers ? (
            <button
              type="button"
              onClick={() => setShowAnswers(true)}
              className={`flex w-full min-h-12 items-center justify-center rounded-2xl px-5 text-sm font-bold sm:min-h-14 sm:text-base ${cardTheme.btnOutline}`}
            >
              عرض الإجابات الصحيحة
            </button>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <h4 className={`text-base font-bold sm:text-lg ${cardTheme.title}`}>مراجعة الإجابات</h4>
              {questions.map((q, qIndex) => (
                <div
                  key={qIndex}
                  className={`${cardTheme.shell} w-full max-w-full min-w-0 overflow-hidden p-3.5 sm:p-5`}
                >
                  <p
                    className={`mb-3 break-words text-sm font-bold leading-relaxed sm:text-base ${cardTheme.title}`}
                  >
                    <span className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-green-700 text-xs text-white">
                      {qIndex + 1}
                    </span>
                    {q.text}
                  </p>
                  <div className="space-y-2">
                    {q.answers.map((answer, aIndex) => (
                      <div
                        key={aIndex}
                        className={`flex w-full max-w-full min-w-0 items-center justify-between gap-2 rounded-xl border p-3 text-right text-sm sm:p-3.5 ${getAnswerStatusClass(qIndex, aIndex)}`}
                      >
                        <span className="min-w-0 flex-1 break-words leading-snug">
                          {formatAnswerText(answer.text, isTrueFalseQuestion(q))}
                        </span>
                        {answer.isCorrect && (
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                        )}
                        {selectedAnswers[qIndex] === aIndex && !answer.isCorrect && (
                          <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={onComplete}
            className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold sm:min-h-14 sm:text-base ${cardTheme.btnGold}`}
          >
            إنهاء
          </button>
        </div>
      )}
    </div>
  );
};
